import { AlertTriangle } from '@untitledui/icons';
import React, { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';

/**
 * Error boundary props.
 */
export interface ErrorBoundaryProps {
  /**
   * Child components to wrap
   */
  children: ReactNode;

  /**
   * Fallback UI to show when an error occurs.
   * Can be a ReactNode or a function that receives the error.
   * A function fallback may return `undefined` to opt back into the default
   * error UI for that error (useful when only one class of error gets custom
   * treatment). Return `null` to intentionally render nothing.
   */
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);

  /**
   * Callback when an error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;

  /**
   * Whether to log errors to console (default: true in development)
   */
  logErrors?: boolean;

  /**
   * Custom error message to display
   */
  errorMessage?: string;

  /**
   * Whether to show a reset button (default: true)
   */
  showReset?: boolean;

  /**
   * Custom reset button text (default: "Try again")
   */
  resetButtonText?: string;
}

/**
 * Error boundary state.
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * React Error Boundary component.
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 *
 * @example
 * ```typescript
 * <ErrorBoundary
 *   fallback={<div>Something went wrong</div>}
 *   onError={(error, errorInfo) => logToService(error, errorInfo)}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, logErrors = process.env.NODE_ENV === 'development' } =
      this.props;

    // Log to console in development
    if (logErrors) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo);
    }

    // Store error info in state
    this.setState({
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const {
      children,
      fallback,
      errorMessage = 'Something went wrong',
      showReset = true,
      resetButtonText = 'Try again',
    } = this.props;

    if (hasError && error) {
      // Custom fallback
      if (fallback) {
        if (typeof fallback === 'function') {
          if (errorInfo) {
            const rendered = fallback(error, errorInfo);
            if (rendered !== undefined) {
              return rendered;
            }
            // Function returned undefined: it declined to customize this
            // error — fall through to the default UI below.
          }
          // If errorInfo is null, we can't call the function safely
          // Fall through to default UI
        } else {
          return fallback;
        }
      }

      // Default fallback UI
      return (
        <div className='flex flex-col items-center justify-center p-8 text-center'>
          <div className='mb-4'>
            <FeaturedIcon icon={AlertTriangle} size='lg' color='error' />
          </div>

          <div className='text-primary font-medium mb-2'>{errorMessage}</div>

          {process.env.NODE_ENV === 'development' && (
            <div className='text-sm text-tertiary mb-4 max-w-2xl'>
              <div className='font-mono text-xs bg-bg-secondary p-4 rounded-lg text-left overflow-auto'>
                <div className='font-bold mb-2'>Error:</div>
                <div className='mb-4'>{error.toString()}</div>
                {errorInfo && (
                  <>
                    <div className='font-bold mb-2'>Component Stack:</div>
                    <pre className='whitespace-pre-wrap text-xs'>
                      {errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            </div>
          )}

          {showReset && (
            <Button color='primary' size='md' onClick={this.handleReset}>
              {resetButtonText}
            </Button>
          )}
        </div>
      );
    }

    return children;
  }
}

/**
 * Hook to use error boundaries functionally.
 *
 * Returns a function to throw errors that will be caught by the nearest ErrorBoundary.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with ErrorBoundary component
export function useErrorHandler(): (error: Error) => void {
  const [, setError] = React.useState<Error | null>(null);

  return React.useCallback((error: Error) => {
    // Throw inside state setter so ErrorBoundary catches it during render
    setError(() => {
      throw error;
    });
    // Don't throw synchronously - let React's render cycle catch it
  }, []);
}

/**
 * Wrapper component that provides error boundary with standard styling.
 */
export function ErrorBoundaryWrapper({
  children,
  ...props
}: Omit<ErrorBoundaryProps, 'children'> & { children: ReactNode }) {
  return <ErrorBoundary {...props}>{children}</ErrorBoundary>;
}
